import { useEffect, useState } from "react";
import { Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const Seg = ({ value, onChange, options, testId }) => (
  <div className="inline-flex flex-wrap bg-[#0F172A] border border-slate-800 rounded p-0.5" data-testid={testId}>
    {options.map(([v, l]) => (
      <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testId}-${v}`}
        className={`px-3 py-1.5 rounded text-xs font-mono transition-colors ${value === v ? "bg-emerald-500/15 text-emerald-300" : "text-slate-400 hover:text-white"}`}>{l}</button>
    ))}
  </div>
);

export const pickPrintRaces = ({ scope, meetings, chosen, prefOnly, ticked, shown }) => {
  if (scope === "preferred") return shown.filter((r) => r.evaluation.preferred);
  if (scope === "ticked") return shown.filter((r) => ticked.includes(r.id));
  const list = scope === "meetings" ? meetings.filter((g) => chosen.includes(g.key)).flatMap((g) => g.races) : shown;
  return prefOnly ? list.filter((r) => r.evaluation.preferred) : list;
};

export const PrintOptions = ({ open, preset, onClose, onPrint, meetings, shown, ticked }) => {
  const [order, setOrder] = useState("rating");
  const [scope, setScope] = useState("all");
  const [chosen, setChosen] = useState([]);
  const [prefOnly, setPrefOnly] = useState(false);
  useEffect(() => { if (open) { setScope(preset || "all"); setPrefOnly(false); } }, [open, preset]);

  const races = pickPrintRaces({ scope, meetings, chosen, prefOnly, ticked, shown });
  const showPref = scope === "all" || scope === "meetings";
  const toggle = (k) => setChosen(chosen.includes(k) ? chosen.filter((x) => x !== k) : [...chosen, k]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0B1220] border-slate-800 max-w-lg" data-testid="print-options-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Print options</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div>
            <div className="text-xs text-slate-400 mb-1.5">Runner order</div>
            <Seg value={order} onChange={setOrder} options={[["number", "Tab No."], ["rating", "Rating"]]} testId="print-order" />
          </div>
          <div>
            <div className="text-xs text-slate-400 mb-1.5">Races to print</div>
            <Seg value={scope} onChange={setScope} testId="print-scope"
              options={[["all", "All meetings"], ["meetings", "Choose meetings"], ["preferred", "Preferred races only"], ["ticked", `Ticked races (${ticked.length})`]]} />
          </div>
          {scope === "meetings" && (
            <div className="space-y-2 max-h-56 overflow-y-auto pr-1" data-testid="print-meetings-list">
              <button type="button" className="text-[11px] font-mono text-emerald-300" data-testid="print-meetings-all"
                onClick={() => setChosen(chosen.length === meetings.length ? [] : meetings.map((g) => g.key))}>{chosen.length === meetings.length ? "Clear all" : "Select all"}</button>
              {meetings.map((g, i) => (
                <label key={g.key} className="flex items-center gap-3 text-sm text-slate-200 cursor-pointer">
                  <Checkbox checked={chosen.includes(g.key)} onCheckedChange={() => toggle(g.key)} data-testid={`print-meeting-${i}`} />
                  <span>{[g.venue, g.state].filter(Boolean).join(" • ")}</span>
                  <span className="text-xs text-slate-500 num">{g.races.length} races • {g.races.filter((r) => r.evaluation.preferred).length} preferred</span>
                </label>
              ))}
            </div>
          )}
          {showPref && (
            <label className="flex items-center gap-3 text-sm text-slate-200">
              <Switch checked={prefOnly} onCheckedChange={setPrefOnly} data-testid="print-preferred-only-switch" />
              Preferred races only{scope === "meetings" ? " (within chosen meetings)" : ""}
            </label>
          )}
        </div>
        <DialogFooter className="items-center gap-3">
          <span className="text-xs text-slate-400 num mr-auto" data-testid="print-race-count">{races.length} race(s) will print</span>
          <Button variant="outline" onClick={onClose} data-testid="print-cancel">Cancel</Button>
          <Button disabled={!races.length} onClick={() => onPrint({ races, order, label: { all: "", meetings: " (chosen meetings)", preferred: " (preferred races)", ticked: " (ticked races)" }[scope] + (showPref && prefOnly && scope !== "preferred" ? " • preferred only" : "") })} data-testid="print-confirm">
            <Printer className="h-4 w-4 mr-2" />Print
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
